import { withDependencies, named } from '@wix/thunderbolt-ioc'
import { PageFeatureConfigSymbol, Props } from '@wix/thunderbolt-symbols'
import type { SliderGalleryPageConfig } from './types'
import { name } from './symbols'
import fastdom from 'fastdom'
import type { ComponentWillMount, ViewerComponent } from 'feature-components'

let isPaused = false
let allItemsContainerReference: HTMLElement | null = null

const extractCompId = (maybeRepeaterId: string) =>
	maybeRepeaterId.includes('__') ? maybeRepeaterId.split('__')[0] : maybeRepeaterId

const animationOptions: KeyframeAnimationOptions = {
	delay: 0,
	iterations: 600,
	playbackRate: 0,
	fill: 'both',
}

const sliderGalleryFactory = (pageFeatureConfig: SliderGalleryPageConfig): ComponentWillMount<ViewerComponent> => {
	const { galleryConfigs } = pageFeatureConfig
	let allItemsWidth: number, visibleItemsWidth: number, maxLeft: number, duration: number, loop: boolean
	const play = async (
		galleryId: string,
		direction: 'left' | 'right',
		visibleItemsContainerRef: HTMLDivElement | null,
		allItemsContainerRef: HTMLDivElement | null
	) => {
		allItemsContainerReference = allItemsContainerRef
		isPaused = false
		if (!visibleItemsContainerRef || !allItemsContainerRef) {
			return
		}
		const galleryConfig = galleryConfigs[galleryId] || galleryConfigs[extractCompId(galleryId)]
		const { speed, margin } = galleryConfig
		loop = galleryConfig.loop

		fastdom.measure(() => {
			allItemsWidth = allItemsContainerRef.offsetWidth - margin || 0
			visibleItemsWidth = visibleItemsContainerRef.offsetWidth + margin || 0
			maxLeft = loop ? -allItemsWidth / 2 : -allItemsWidth + visibleItemsWidth
			duration = (Math.abs(maxLeft) * 20) / speed
		})

		fastdom.mutate(() => {
			animationOptions.duration = duration
			if (!loop) {
				animationOptions.iterations = 1
			}
			const animationData = loop
				? [{ transform: 'translateX(0px)' }, { transform: 'translateX(-50%)' }]
				: [{ transform: 'translateX(0%)' }, { transform: `translateX(calc(-100% + ${visibleItemsWidth}px))` }]

			let animation = allItemsContainerRef.getAnimations()[0]
			if (!animation) {
				animation = allItemsContainerRef.animate(animationData, animationOptions)
			}

			animation.playbackRate = 0
			animation.play()
			const start = Date.now()
			const entranceAnimation = (to: number) => {
				const now = Date.now()
				const elapsed = now - start
				const interval = to * (elapsed / (4000 * speed))
				if (!loop) {
					const currentTime = (animation.currentTime as number) || 0
					const onEdges = to === 1 ? currentTime > duration - 100 : currentTime < 100
					if (onEdges) {
						animation.updatePlaybackRate(to)
						return
					}
				}
				// if the current animation's playbackRate is less than the target - increase it
				if (Math.abs(animation.playbackRate) < 1) {
					// if user hovered-out -> stop the recursive calls of entrance animation
					if (isPaused) {
						return
					}
					animation.updatePlaybackRate(animation.playbackRate + interval)
					requestAnimationFrame(() => entranceAnimation(to))
				} else {
					animation.updatePlaybackRate(to)
				}
			}

			if (direction === 'left') {
				entranceAnimation(-1)
			} else {
				entranceAnimation(1)
			}

			if (loop) {
				animation.onfinish = () => {
					animation.play()
				}
			}
		})
	}

	const pause = async (galleryId: string) => {
		isPaused = true
		if (allItemsContainerReference) {
			const animation = allItemsContainerReference.getAnimations()[0]
			const galleryConfig = galleryConfigs[galleryId] || galleryConfigs[extractCompId(galleryId)]
			const { speed } = galleryConfig
			if (animation) {
				const start = Date.now()
				const leaveAnimation = () => {
					const now = Date.now()
					const elapsed = now - start
					const interval = 1 * (elapsed / (4000 * speed))
					if (!loop) {
						const currentTime = (animation.currentTime as number) || 0
						const onEdges = currentTime > duration - 100
						if (onEdges) {
							animation.updatePlaybackRate(0)
							return
						}
					}
					if (Math.abs(animation.playbackRate) > interval) {
						const pbr =
							animation.playbackRate > 0 ? animation.playbackRate - interval : animation.playbackRate + interval
						animation.updatePlaybackRate(pbr)
						requestAnimationFrame(() => leaveAnimation())
					} else {
						animation.updatePlaybackRate(0)
					}
				}

				leaveAnimation()
			}
		}
	}

	return {
		componentTypes: ['SliderGallery'],
		async componentWillMount(component) {
			component.updateProps({
				play,
				pause,
			})
		},
	}
}

export const SliderGallery = withDependencies([named(PageFeatureConfigSymbol, name), Props], sliderGalleryFactory)
